import { StringDevUIFieldDescriptor as UIFieldDescriptor } from "../../core/ui/StringDevUISchema";
import { GridAdapter } from "./GridAdapter";

/**
 * Pentagram adapter.
 *
 * Draws a regular pentagram (five-pointed star) inscribed in the element.
 * The pentagram naturally encodes golden-ratio proportions — every
 * intersection divides a diagonal at φ:1, making it a powerful
 * compositional skeleton for logos, icons, and symmetric layouts.
 *
 * Modes:
 * - Fit:     preserves proportions, fits inside the element
 * - Fill:    preserves proportions, covers the entire element (may clip)
 * - Stretch: stretches independently by width and height to fill fully
 */
export class PentagramAdapter extends GridAdapter {
  readonly type = "pentagram";
  readonly label = "Pentagram";
  readonly icon = `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><polygon points="8,1 9.8,6.2 15,6.2 10.8,9.5 12.4,15 8,11.5 3.6,15 5.2,9.5 1,6.2 6.2,6.2" stroke="currentColor" stroke-opacity="0.5" fill="none" stroke-width="0.8"/></svg>`;

  getDefaults(): Record<string, any> {
    return {
      sizing: "stretch",
      rotation: 0,
      showPentagon: true,
      showStar: true,
      color: "#b07ee8",
      opacity: 0.45,
    };
  }

  getUISchema(): UIFieldDescriptor[] {
    return [
      {
        type: "select",
        key: "sizing",
        label: "Sizing",
        default: "stretch",
        options: [
          { value: "stretch", label: "Stretch" },
          { value: "fit", label: "Fit" },
          { value: "fill", label: "Fill" },
        ],
      },
      { type: "range", key: "rotation", label: "Rotation", default: 0, min: 0, max: 360, step: 1 },
      { type: "toggle", key: "showPentagon", label: "Pentagon", default: true },
      { type: "toggle", key: "showStar", label: "Star", default: true },
      { type: "divider" },
      { type: "color", key: "color", label: "Color", default: "#b07ee8" },
      { type: "range", key: "opacity", label: "Opacity", default: 0.45, min: 0, max: 1, step: 0.01 },
    ];
  }

  render(
    svg: SVGSVGElement,
    width: number,
    height: number,
    settings: Record<string, any>,
  ): void {
    const group = this.getGroup(svg, settings.__instanceId);
    const { sizing, rotation, showPentagon, showStar, color, opacity } = settings;

    const pad = 1;
    const rotRad = (rotation - 90) * Math.PI / 180;

    // 1. Compute vertices on a unit circle
    const unit: [number, number][] = [];
    for (let i = 0; i < 5; i++) {
      const angle = rotRad + i * (2 * Math.PI / 5);
      unit.push([Math.cos(angle), Math.sin(angle)]);
    }

    // 2. Bounding box of unit vertices
    let minX = unit[0][0], maxX = unit[0][0];
    let minY = unit[0][1], maxY = unit[0][1];
    for (const [ux, uy] of unit) {
      if (ux < minX) minX = ux;
      if (ux > maxX) maxX = ux;
      if (uy < minY) minY = uy;
      if (uy > maxY) maxY = uy;
    }
    const bw = maxX - minX;
    const bh = maxY - minY;
    const bcx = (minX + maxX) / 2;
    const bcy = (minY + maxY) / 2;

    // 3. Scale to fit element by sizing mode
    let sx: number, sy: number;
    switch (sizing) {
      case "fit": {
        const s = Math.min(width / bw, height / bh) * pad;
        sx = s; sy = s;
        break;
      }
      case "fill": {
        const s = Math.max(width / bw, height / bh) * pad;
        sx = s; sy = s;
        break;
      }
      default: // stretch
        sx = (width / bw) * pad;
        sy = (height / bh) * pad;
        break;
    }

    // 4. Transform to element coordinates (centered)
    const verts: [number, number][] = unit.map(([ux, uy]) => [
      width / 2 + (ux - bcx) * sx,
      height / 2 + (uy - bcy) * sy,
    ]);

    // Pentagon outline: 0→1→2→3→4→0
    if (showPentagon) {
      const d = verts.map((v, i) => `${i === 0 ? "M" : "L"} ${v[0]} ${v[1]}`).join(" ") + " Z";
      group.appendChild(this.createPath(d, color, opacity * 0.5, 1));
    }

    // Pentagram star: connect every other vertex 0→2→4→1→3→0
    if (showStar) {
      const order = [0, 2, 4, 1, 3];
      const d = order.map((idx, i) => {
        const v = verts[idx];
        return `${i === 0 ? "M" : "L"} ${v[0]} ${v[1]}`;
      }).join(" ") + " Z";
      group.appendChild(this.createPath(d, color, opacity, 1));
    }
  }
}
